/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-05-25 22:25:50
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Field } from 'redux-form';
import { ButtonGroup, Button, Icon, Link, Tooltip, TextStyle } from '@shopify/polaris';
import {
  EditMajor,
  DeleteMajor,
  MobileVerticalDotsMajor,
  QuestionMarkMajor
} from '@shopify/polaris-icons';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import type { DropResult } from 'react-beautiful-dnd';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';


import EditModal from 'src/components/containers/tools/custom-sorting/modal';
import NoCustomSorting from './no-custom-sorting';
import LoadingRows from './loading-rows';
import TableCell from './table-cell';
import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';
import { getTypeValue } from 'src/helpers/custom-sorting-helper';
import { URL } from 'src/constants/links';
import { NUMBER_CUSTOM_SORTING, SORT_TYPES_ORDER } from 'src/constants/custom-sorting';

import ConfirmDeleteModal from './confirm-delete-modal';
import ConfirmDisableModal from './confirm-disable-modal';

type Props = {
  fields: Array<Object>,
  loading: boolean,
  success: boolean,
  handleActiveModalEdit: () => void,
  updatePositionFlag: () => void
};

type ComposedProps = Props & WithI18nProps;

type State = {
  activeModalEdit: boolean,
  activeModalRemove: boolean,
  activeModalDisable: boolean,
  selectedId: number,
  isUsedInDefaultSorting: boolean
};

class ListCustomSorting extends Component<ComposedProps, State> {
  state = {
    activeModalEdit: false,
    activeModalRemove: false,
    activeModalDisable: false,
    selectedId: -1,
    isUsedInDefaultSorting: false
  };

  onDragEnd = (result: DropResult) => {
    const { fields, updatePositionFlag } = this.props;

    // dropped outside the list
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    // no movement
    if (result.destination.index === result.source.index) {
      return;
    }

    fields.move(result.source.index, result.destination.index);
    updatePositionFlag(true);
  };

  render() {
    const { i18n, fields, sortOptions, loading } = this.props;
    const {
      activeModalRemove,
      isUsedInDefaultSorting,
      activeModalEdit,
      selectedId,
      activeModalDisable
    } = this.state;
    // Check having custom sorting
    const hasSortOption = !_.isEmpty(sortOptions);

    return (
      <>
        {hasSortOption && sortOptions.length <= NUMBER_CUSTOM_SORTING && (
          <div className="addNew top">
            <Button plain onClick={() => this.handleActiveModalEdit(-1)}>
              {i18n.translate('AdminHTML.CustomSorting.CustomSorting.addNew')}
            </Button>
          </div>
        )}
        <DragDropContext onDragEnd={this.onDragEnd}>
          <div className="Polaris-DataTable">
            <div className="Polaris-DataTable__ScrollContainer" style={{ marginLeft: 0 }}>
              <table className="Polaris-DataTable__Table">
                <thead>
                  <tr>
                    <th
                      className={`Polaris-DataTable__Cell Polaris-DataTable__Cell--firstColumn Polaris-DataTable__Cell--header ${
                        fields.length === 0 ? 'EmptyCell' : ''
                      }`}
                    />
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                      {i18n.translate('AdminHTML.CustomSorting.CustomSorting.status')}
                    </th>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                      {i18n.translate('AdminHTML.CustomSorting.CustomSorting.label')}
                    </th>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                      {i18n.translate('AdminHTML.CustomSorting.CustomSorting.type')}
                    </th>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                      {i18n.translate('AdminHTML.CustomSorting.CustomSorting.order')}
                    </th>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--numeric">
                      {i18n.translate('AdminHTML.CustomSorting.CustomSorting.actions')}
                    </th>
                  </tr>
                </thead>
                {!_.isEmpty(fields) ? (
                  <Droppable droppableId="table">
                    {provided => (
                      <tbody ref={provided.innerRef} {...provided.droppableProps}>
                        {fields.map((item, key) => {
                          const { type = 'type', label = '', order } = sortOptions[key] || [];
                          return (
                            // Enable drag for per option row
                            <Draggable
                              draggableId={`${type}-${key}`}
                              key={`${type}-${key}`}
                              index={key}>
                              {(provided, snapshot) => (
                                <tr
                                  className="Polaris-DataTable__TableRow"
                                  ref={provided.innerRef}
                                  {...provided.dragHandleProps}
                                  {...provided.draggableProps}>
                                  {/* Drag icon */}
                                  <TableCell
                                    isDragOccurring={snapshot.isDragging}
                                    className="Polaris-DataTable__Cell Polaris-DataTable__Cell--firstColumn">
                                    <Icon color="subdued" source={MobileVerticalDotsMajor} />
                                  </TableCell>

                                  {/* Status */}
                                  <TableCell
                                    isDragOccurring={snapshot.isDragging}
                                    className="Polaris-DataTable__Cell">
                                    <Field
                                      name={`${item}.status`}
                                      component={RenderToggleSwitch}
                                      parse={value => (value ? 'active' : 'disabled')}
                                      // $FlowFixMe
                                      format={value => value === 'active'}
                                      onChange={e => this.handleChangeStatus(e, key)}
                                    />
                                  </TableCell>

                                  {/* Label */}
                                  <TableCell
                                    isDragOccurring={snapshot.isDragging}
                                    className="Polaris-DataTable__Cell">
                                    <Link
                                      removeUnderline
                                      onClick={() => {
                                        this.handleActiveModalEdit(key);
                                      }}>
                                      {_.isEmpty(label) ? '-' : label}
                                    </Link>
                                  </TableCell>

                                  {/* Type */}
                                  <TableCell
                                    isDragOccurring={snapshot.isDragging}
                                    className="Polaris-DataTable__Cell">
                                    {type}
                                    {this.renderTooltip(type)}
                                  </TableCell>

                                  {/* Order */}
                                  <TableCell
                                    isDragOccurring={snapshot.isDragging}
                                    className="Polaris-DataTable__Cell">
                                    {this.renderOrderDisplay(type, order)}
                                  </TableCell>

                                  {/* Action */}
                                  <TableCell
                                    isDragOccurring={snapshot.isDragging}
                                    className="Polaris-DataTable__Cell Polaris-DataTable__Cell-Action">
                                    {this.renderAction(key)}
                                  </TableCell>
                                </tr>
                              )}
                            </Draggable>
                          );
                        })}
                        {loading && (
                          // Loading rows while reading custom range sliders
                          <LoadingRows />
                        )}
                      </tbody>
                    )}
                  </Droppable>
                ) : (
                  <tbody>
                    {loading && (
                      // Loading rows while reading custom range sliders
                      <LoadingRows />
                    )}
                  </tbody>
                )}
              </table>

              {/* No custom range sliders card */}
              {_.isEmpty(fields) && !loading ? (
                <NoCustomSorting handleChange={this.handleActiveModalEdit} />
              ) : (
                ''
              )}
            </div>
          </div>
        </DragDropContext>
        {hasSortOption ? (
          sortOptions.length <= NUMBER_CUSTOM_SORTING ? (
            <div className="addNew">
              <Button plain onClick={() => this.handleActiveModalEdit(-1)}>
                {i18n.translate('AdminHTML.CustomSorting.CustomSorting.addNew')}
              </Button>
            </div>
          ) : (
            <div style={{ padding: '1.25rem' }}>
              <p>
                <TextStyle variation="negative">
                  <TextStyle variation="strong">
                    {i18n.translate('AdminHTML.Common.Title.notice')}
                  </TextStyle>
                </TextStyle>{' '}
                {i18n.translate('AdminHTML.CustomSorting.CustomSorting.reachedLimit', {
                  nt_number_custom_sorting: NUMBER_CUSTOM_SORTING,
                  t1: (
                    <Link external url={URL.CustomSortingDoc}>
                      {i18n.translate('AdminHTML.CustomSorting.CustomSorting.reachedLimit_t1')}
                    </Link>
                  )
                })}
              </p>
            </div>
          )
        ) : (
          ''
        )}

        {/* Confirm delete modal */}
        <ConfirmDeleteModal
          active={activeModalRemove}
          handleChange={this.handleActiveOptionModalRemove}
          handleRemove={this.deleteSortOption}
          title={i18n.translate('AdminHTML.CustomSorting.CustomSorting.confirmDelete_Title')}
          isUsedInDefaultSorting={isUsedInDefaultSorting}
        />

        {/* Confirm disable modal */}
        <ConfirmDisableModal
          active={activeModalDisable}
          handleChange={this.handleActiveOptionModalDisable}
          handleDisable={this.disableSortOption}
          title={i18n.translate('AdminHTML.CustomSorting.CustomSorting.confirmDisable_Title')}
          isUsedInDefaultSorting={isUsedInDefaultSorting}
        />

        {/* Create/Edit modal */}
        <EditModal
          active={activeModalEdit}
          handleChange={this.handleActiveModalEdit}
          sortOption={sortOptions[selectedId] || {}}
          sortOptions={sortOptions}
          index={selectedId}
          updateSortOption={this.updateSortOption}
        />
      </>
    );
  }

  // Render order display name for per option
  renderOrderDisplay = (type, order) => {
    const { i18n } = this.props;

    if (_.isEmpty(order)) {
      return '';
    }

    const { sortTypes } = this.props;
    const typeName = getTypeValue(type, sortTypes);
    const orderOptions = !_.isEmpty(typeName) ? SORT_TYPES_ORDER[typeName] : [];

    const orderOption = _.find(orderOptions, item => item.value === order);

    return !_.isEmpty(orderOption) ? i18n.translate(orderOption.label) : order;
  };

  // Render the tooltip beside the type label
  renderTooltip = type => {
    const { i18n } = this.props;
    const useOnCollectionOnly = ['manual'];
    let messagePage = '';
    if (useOnCollectionOnly.includes(type)) {
      messagePage = i18n.translate(
        'AdminHTML.CustomSorting.CustomSorting.type_Tooltip_t1_collection'
      );
    }

    if (messagePage !== '') {
      return (
        <span className="tooltip long-tooltip">
          <Tooltip
            content={
              <span className="tooltipContent">
                {i18n.translate('AdminHTML.CustomSorting.CustomSorting.type_Tooltip', {
                  t1: messagePage
                })}
              </span>
            }
            preferredPosition="above">
            <Icon source={QuestionMarkMajor} color="base" />
          </Tooltip>
        </span>
      );
    }
    return '';
  };
  // Render action button for per option
  renderAction = (id: number) => {
    return (
      <ButtonGroup segmented>
        <Button
          size="slim"
          icon={EditMajor}
          onClick={() => {
            this.handleActiveModalEdit(id);
          }}
        />
        <Button
          size="slim"
          icon={DeleteMajor}
          onClick={() => {
            this.updateSelected(id);
            this.setState({ isUsedInDefaultSorting: this.checkUsedInDefaultSorting(id) });
            this.handleActiveOptionModalRemove();
          }}
        />
      </ButtonGroup>
    );
  };

  handleActiveModalEdit = id => {
    const { activeModalEdit } = this.state;

    if (activeModalEdit) {
      this.setState({ activeModalEdit: !activeModalEdit, selectedId: -1 });
    } else {
      this.setState({ activeModalEdit: !activeModalEdit, selectedId: id });
    }
  };

  checkUsedInDefaultSorting = id => {
    const { sortOptions, defaultSorting = {} } = this.props;

    const sortOption = sortOptions[id];
    let search = _.get(defaultSorting, 'search', '');
    let allCollection = _.get(defaultSorting, 'allCollection', '');
    const specificCollections = _.get(defaultSorting, 'specificCollections', {});
    let isUsed = false;
    if (search === sortOption.id) {
      isUsed = true;
    } else if (allCollection === sortOption.id) {
      isUsed = true;
    } else {
      const findItem = _.find(specificCollections, item => item.sortId === sortOption.id);
      if (!_.isEmpty(findItem)) {
        isUsed = true;
      }
    }
    return isUsed;
  };

  // Update selected id
  updateSelected = id => {
    this.setState({ selectedId: id });
  };

  // Show/Hide Remove modal
  handleActiveOptionModalRemove = () => {
    this.setState(({ activeModalRemove }) => ({
      activeModalRemove: !activeModalRemove
    }));
  };

  /**
   * Delete custom sorting item
   */
  deleteSortOption = () => {
    const { selectedId, isUsedInDefaultSorting } = this.state;
    const {
      fields,
      updatePositionFlag,
      sortOptions,
      defaultSorting = {},
      updateCustomSortingForm
    } = this.props;

    const sortOption = sortOptions[selectedId];

    // Update default sorting if needed
    if (isUsedInDefaultSorting) {
      let search = _.get(defaultSorting, 'search', '');
      let allCollection = _.get(defaultSorting, 'allCollection', '');
      let specificCollections = _.get(defaultSorting, 'specificCollections', {});
      if (search === sortOption.id) {
        search = '';
      }
      if (allCollection === sortOption.id) {
        allCollection = '';
      }
      specificCollections = JSON.parse(JSON.stringify(specificCollections));
      const removed = _.remove(specificCollections, item => item.sortId === sortOption.id);
      if (_.isEmpty(removed)) {
      }

      let data = Object.assign({}, defaultSorting);
      data = { ...data, search, allCollection, specificCollections };

      updateCustomSortingForm('defaultSorting', data);
    }

    // Remove item
    fields.remove(selectedId);

    updatePositionFlag(true);
    this.setState({ selectedId: -1 });
    this.handleActiveOptionModalRemove();

  };

  /*
   * Update custom sorting
   */
  updateSortOption = (data, index) => {
    if (index === -1) {
      // New custom sorting
      const { fields } = this.props;
      fields.push({ ...data, position: fields.length });
    } else {
      // Update fields sort options
      const { updateCustomSortingForm } = this.props;
      updateCustomSortingForm(`sortOptions[${index}]`, data);
    }
  };

  // Show/Hide disable modal
  handleActiveOptionModalDisable = () => {
    this.setState(({ activeModalDisable }) => ({
      activeModalDisable: !activeModalDisable
    }));
  };

  // Handle change status
  handleChangeStatus = (e, index) => {
    const { sortOptions } = this.props;
    const sortOption = sortOptions[index];

    if (sortOption.status === 'active') {
      const isUsedInDefaultSorting = this.checkUsedInDefaultSorting(index);
      if (isUsedInDefaultSorting) {
        e.preventDefault();
        this.updateSelected(index);
        this.handleActiveOptionModalDisable();
      }
    }
  };

  /**
   * Disable custom sorting item (for item which is use in default sorting)
   */
  disableSortOption = () => {
    const { selectedId } = this.state;
    const { sortOptions, defaultSorting = {}, updateCustomSortingForm } = this.props;

    const sortOption = sortOptions[selectedId];

    // Update default sorting
    let search = _.get(defaultSorting, 'search', '');
    let allCollection = _.get(defaultSorting, 'allCollection', '');
    let specificCollections = _.get(defaultSorting, 'specificCollections', {});
    if (search === sortOption.id) {
      search = '';
    }
    if (allCollection === sortOption.id) {
      allCollection = '';
    }
    specificCollections = JSON.parse(JSON.stringify(specificCollections));
    const removed = _.remove(specificCollections, item => item.sortId === sortOption.id);
    if (_.isEmpty(removed)) {
    }

    let data = Object.assign({}, defaultSorting);
    data = { ...data, search, allCollection, specificCollections };

    updateCustomSortingForm('defaultSorting', data);

    // Update item
    updateCustomSortingForm(`sortOptions[${selectedId}].status`, 'disabled');

    this.setState({ selectedId: -1 });
    this.handleActiveOptionModalDisable();

  };
}

export default withI18n()(ListCustomSorting);
